/**
 * トピックマップデータ処理フック
 * 競合分析データからSunburstチャート用のデータを生成・管理
 */

import { useMemo } from 'react';
import {
  extractTopicMapData,
  createScoreMap,
  buildHierarchicalData,
  generateSunburstOption,
  generateTreeOption,
  generateEmptyData,
} from '../utils/topicMapHelpers';

/**
 * トピックマップデータを処理するカスタムフック
 * @param {Object} competitorData - 競合分析データ
 * @param {Object} options - オプション設定
 * @returns {Object} 処理されたトピックマップデータ
 */
export const useTopicMapData = (competitorData, options = {}) => {
  const {
    enableAnimation = true,
    showVisualMap = true,
    height = 500,
  } = options;

  // トピック関連のみを対象にして再計算頻度を抑制（ちらつき防止）
  const topicPayload = useMemo(() => {
    return { topic_analysis: competitorData?.topic_analysis };
  }, [competitorData?.topic_analysis]);

  // 基本データの抽出とメモ化
  const extractedData = useMemo(() => {
    console.log('🔄 [useTopicMapData] Processing topic_analysis payload...');
    return extractTopicMapData(topicPayload);
  }, [topicPayload]);

  // スコアマップの生成とメモ化
  const scoreMap = useMemo(() => {
    console.log('🔄 [useTopicMapData] Creating score map...');
    return createScoreMap(extractedData.topicScores);
  }, [extractedData.topicScores]);

  // 階層データの構築とメモ化
  const hierarchicalData = useMemo(() => {
    console.log('🔄 [useTopicMapData] Building hierarchical data...');

    if (!extractedData.hasData) {
      return generateEmptyData(extractedData.mainQuery);
    }

    return buildHierarchicalData(
      extractedData.mainQuery,
      extractedData.categories,
      scoreMap,
    );
  }, [
    extractedData.mainQuery,
    extractedData.categories,
    extractedData.hasData,
    scoreMap,
  ]);

  // EChartsオプションの生成とメモ化
  const chartOption = useMemo(() => {
    console.log('🔄 [useTopicMapData] Generating chart options...');

    return generateTreeOption(hierarchicalData, {
      height,
      showVisualMap,
      enableAnimation,
    });
  }, [hierarchicalData, height, showVisualMap, enableAnimation]);

  // 統計情報の計算
  const statistics = useMemo(() => {
    const totalCategories = extractedData.categories.length;
    const totalTopics = extractedData.categories.reduce(
      (sum, category) => sum + (category.unified_topics?.length || 0),
      0,
    );

    // スコアの有効な値のみを使用
    const validScores = Array.from(scoreMap.values()).filter(
      score => typeof score === 'number' && !isNaN(score) && score > 0,
    );

    const averageScore =
      validScores.length > 0
        ? validScores.reduce((sum, score) => sum + score, 0) /
          validScores.length
        : 0;
    const maxScore = validScores.length > 0 ? Math.max(...validScores) : 0;
    const minScore = validScores.length > 0 ? Math.min(...validScores) : 0;

    console.log('📊 [useTopicMapData] Statistics calculated:', {
      totalCategories,
      totalTopics,
      scoreMapSize: scoreMap.size,
      validScoresCount: validScores.length,
      averageScore,
      maxScore,
      minScore,
      hasData: extractedData.hasData,
    });

    return {
      totalCategories,
      totalTopics,
      averageScore,
      maxScore,
      minScore,
      hasData: extractedData.hasData,
    };
  }, [extractedData.categories, extractedData.hasData, scoreMap]);

  // イベントハンドラー
  const handleChartClick = params => {
    console.log('🔍 [TopicMap] Chart clicked:', {
      name: params.data.name,
      value: params.data.value,
      hasChildren: !!params.data.children,
    });

    // カテゴリークリック時の処理
    if (params.data.children && params.data.children.length > 0) {
      console.log('📂 Category clicked:', params.data.name);
      // 必要に応じてカテゴリー詳細表示などの処理を追加
    } else {
      // トピッククリック時の処理
      console.log('📄 Topic clicked:', params.data.name);
      // 必要に応じてトピック詳細表示などの処理を追加
    }
  };

  const handleChartHover = params => {
    // ホバー時の処理（必要に応じて実装）
    console.log('👆 [TopicMap] Chart hovered:', params.data.name);
  };

  // デバッグ情報
  console.log('📊 [useTopicMapData] Hook result:', {
    hasData: extractedData.hasData,
    statistics,
    chartOptionKeys: Object.keys(chartOption),
  });

  return {
    // データ
    hierarchicalData,
    chartOption,
    extractedData,
    statistics,

    // 状態
    isLoading: !competitorData,
    hasData: extractedData.hasData,
    isEmpty: !extractedData.hasData,

    // イベントハンドラー
    onChartClick: handleChartClick,
    onChartHover: handleChartHover,

    // 生データ（デバッグ用）
    rawData: {
      competitorData,
      scoreMap,
      categories: extractedData.categories,
      topicScores: extractedData.topicScores,
    },
  };
};
